import React from "react";

/**
 * LeLe brand mark — uses the official PNG logo (shield with silhouettes,
 * ₹ symbol and verified checkmark) plus a TM trademark superscript in
 * the top-right corner. The tagline "Before you lend. Let's LeLe." is
 * rendered as an optional subline.
 *
 * Props
 * - size:            icon height in px (image auto-widths to preserve ratio)
 * - showWordmark:    render the "LeLe" wordmark alongside the icon
 *                    (the full logo image already includes the wordmark, so
 *                     when true we use the full logo asset; when false we
 *                     crop to the icon-only left region via object-position)
 * - withSubline:     render the "Before you lend. Let's LeLe." tagline
 */
export default function BrandLogo({
  size = 42,
  showWordmark = false,
  className = "",
  withSubline = false,
}) {
  // Full logo (icon + LeLe wordmark) — the ratio in the source PNG is ~2.6:1.
  const fullW = Math.round(size * 2.6);

  // Icon-only crop uses object-position/object-fit to show just the left
  // shield portion of the same asset — no separate file needed.
  if (!showWordmark) {
    return (
      <div
        className={`shrink-0 relative ${className}`}
        style={{ width: size, height: size }}
        aria-label="LeLe"
      >
        <div
          className="w-full h-full rounded-md"
          style={{
            backgroundImage: "url('/brand/lele-logo.png')",
            backgroundSize: "260% 100%",
            backgroundPosition: "left center",
            backgroundRepeat: "no-repeat",
          }}
        />
        <sup
          className="absolute -top-0.5 -right-1 text-[8px] leading-none text-violet-300/80 font-medium"
          aria-hidden="true"
        >
          TM
        </sup>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div
        className="relative shrink-0"
        style={{ width: fullW, height: size }}
      >
        <img
          src="/brand/lele-logo.png"
          alt="LeLe"
          className="block h-full w-full object-contain"
          draggable={false}
        />
        <sup
          className="absolute top-0 right-0 text-[9px] leading-none text-violet-200/85 font-medium tracking-wider select-none"
          aria-hidden="true"
        >
          TM
        </sup>
      </div>

      {withSubline && (
        <>
          <span className="h-8 w-px bg-white/15" aria-hidden="true" />
          <span className="flex flex-col leading-tight text-[11px] md:text-[12px]">
            <span className="text-neutral-200">Before you lend.</span>
            <span className="text-violet-400 font-medium">Let's LeLe.</span>
          </span>
        </>
      )}
    </div>
  );
}
